import get from 'lodash/get';
import queryString from 'query-string';
import {
  getRequest,
  patchRequest,
  patchAllRequest,
  postRequest,
  deleteRequest,
} from '../common/http/lib';
import {
  FILE_REQUEST_REST_API_URL,
  FILE_REQUEST_REST_PUBLIC_API_URL,
  FILE_REQUEST_REST_API_BULK_URL,
  LEGACY_FILE_REQUEST_REST_API_BULK_URL,
  FILE_REQUEST_DASHBOARD_REST_API_URL,
  LEGACY_FILE_REQUEST_DASHBOARD_REST_API_URL,
  MODE,
} from '../constants';
import type { FileRequest } from '../types/FileRequest';
import type { BulkResponse } from '../types/BulkResponse';
import type { FormWithFields } from '../types/form/Form';
import type { FormResponse } from '../types/FormResponse';
import type {
  FileRequestDashboardResponse,
  FileRequestDashboardRow,
  LegacyFileRequestDashboardRow,
} from '../types/FileRequestDashboard';

const getFileRequestForFolder = async (
  folderId: string,
): Promise<FileRequest> =>
  getRequest(`${FILE_REQUEST_REST_API_URL}?folderId=${folderId}`).then(
    response => response.data,
  );

const getFileRequestDashboard = async (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  params: any,
): Promise<FileRequestDashboardResponse> => {
  const queryParams = params ? `?${queryString.stringify(params)}` : '';
  return getRequest(
    `${FILE_REQUEST_DASHBOARD_REST_API_URL}${queryParams}`,
  ).then(response => response.data);
};

const getLegacyFileRequestDashboard = async (): Promise<
  Array<LegacyFileRequestDashboardRow>
> =>
  getRequest(`${LEGACY_FILE_REQUEST_DASHBOARD_REST_API_URL}`).then(
    response => response.data,
  );

const getFileRequest = async (fileRequestId: string): Promise<FileRequest> =>
  getRequest(`${FILE_REQUEST_REST_API_URL}/${fileRequestId}`).then(
    response => response.data,
  );

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const getFileRequests = (params: any): Promise<FileRequest[]> => {
  const queryParams = params ? `?${queryString.stringify(params)}` : '';
  return getRequest(FILE_REQUEST_REST_API_BULK_URL + queryParams).then(
    response => response.data,
  );
};

// GET /app-api/file-request-web/public/file-request
// For anonymous users ONLY
const getPublicFileRequestByUrlId = async (
  urlId: string,
): Promise<FileRequest> =>
  getRequest(`${FILE_REQUEST_REST_PUBLIC_API_URL}?urlId=${urlId}`).then(
    response => response.data,
  );

// GET /app-api/file-request-web/file-request
// For authenticated users ONLY. This will pass through AuthorizationFilter in API Gateway for handling
// various AuthZ checks such as IP allowlisting
// https://jira.inside-box.net/browse/FORMS-6083
const getFileRequestByUrlId = async (urlId: string): Promise<FileRequest> =>
  getRequest(`${FILE_REQUEST_REST_API_URL}?urlId=${urlId}`).then(
    response => response.data,
  );

const updateFileRequest = async (
  fileRequest: FileRequest,
  newFileRequest: FileRequest,
): Promise<FileRequest> =>
  patchRequest(
    `${FILE_REQUEST_REST_API_URL}/${newFileRequest.id}?formVersionId=${get(
      newFileRequest,
      'form.versionId',
    )}`,
    fileRequest,
    newFileRequest,
  ).then(response => response.data);

const updateAllFileRequestDashboardRows = async (
  fileRequestDashboardRows: Array<FileRequestDashboardRow>,
  newFileRequestDashboardRows: Array<FileRequestDashboardRow>,
): Promise<BulkResponse> => {
  const requests = newFileRequestDashboardRows.map((newObject, index) => {
    return {
      oldObject: fileRequestDashboardRows[index],
      newObject,
      context: { formVersionId: get(newObject, 'formVersionId') },
    };
  });
  return patchAllRequest(FILE_REQUEST_REST_API_BULK_URL, requests).then(
    response => response.data,
  );
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const deleteFileRequest = async (fileRequest: FileRequest): Promise<any> =>
  deleteRequest(`${FILE_REQUEST_REST_API_URL}/${fileRequest.id}`);

const deleteFileRequestById = async (fileRequestId: string): Promise<void> =>
  deleteRequest(`${FILE_REQUEST_REST_API_URL}/${fileRequestId}`);

const deleteAllFileRequestsById = async (
  ids: Array<string>,
): Promise<BulkResponse> => {
  let path = '?';
  ids.forEach(id => {
    path = path.concat(`id=${id}&`);
  });
  path = path.slice(0, -1);
  return deleteRequest(`${FILE_REQUEST_REST_API_BULK_URL}${path}`).then(
    response => response.data,
  );
};

const deleteAllLegacyFileRequestsById = async (
  ids: Array<string>,
): Promise<BulkResponse> => {
  let path = '?';
  ids.forEach(id => {
    path = path.concat(`id=${id}&`);
  });
  path = path.slice(0, -1);
  return deleteRequest(`${LEGACY_FILE_REQUEST_REST_API_BULK_URL}${path}`).then(
    response => response.data,
  );
};

const copyFileRequest = async (
  fileRequest: FileRequest,
  folderIds: Array<string> = [],
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
): Promise<any> =>
  postRequest(
    `${FILE_REQUEST_REST_API_URL}/${fileRequest.id}:copy`,
    folderIds.map(folderId => ({
      folderId,
    })),
  ).then(response => response.data);

const uploadFileRequestLogo = async (
  fileRequest: FileRequest,
  file: File,
  previousImagePath?: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
): Promise<any> => {
  const formData = new FormData();

  formData.append('file', file);
  if (previousImagePath) {
    formData.append('previousImagePath', previousImagePath);
  }

  return postRequest(
    `${FILE_REQUEST_REST_API_URL}/${fileRequest.id}:upload-logo`,
    formData,
  ).then(response => response.data);
};

// currently works only with file-requests of type form
const createFileRequest = async (title: string) => {
  const payload: {
    title?: string;
    type: string;
  } = { type: MODE.FORM };
  if (title.length > 0) payload.title = title;
  return postRequest(FILE_REQUEST_REST_API_URL, payload).then(
    response => response.data,
  );
};

const getFormByFileRequestId = async (
  fileRequestId: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  params: any,
): Promise<FormWithFields> => {
  const queryParams = params ? `?${queryString.stringify(params)}` : '';
  return getRequest(
    `${FILE_REQUEST_REST_API_URL}/${fileRequestId}/form/${queryParams}`,
  ).then(response => response.data);
};

const getResponsesByFileRequestId = async (
  fileRequestId: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  params: any,
): Promise<FormResponse[]> => {
  const queryParams = params ? `?${queryString.stringify(params)}` : '';
  return getRequest(
    `${FILE_REQUEST_REST_API_URL}/${fileRequestId}/form-responses/${queryParams}`,
  ).then(response => response.data);
};

const FileRequestAPI = {
  getFileRequest,
  getFileRequests,
  getFileRequestDashboard,
  getFileRequestByUrlId,
  getPublicFileRequestByUrlId,
  getFileRequestForFolder,
  getLegacyFileRequestDashboard,
  updateFileRequest,
  copyFileRequest,
  deleteFileRequest,
  deleteAllFileRequestsById,
  deleteAllLegacyFileRequestsById,
  uploadFileRequestLogo,
  updateAllFileRequestDashboardRows,
  createFileRequest,
  getFormByFileRequestId,
  getResponsesByFileRequestId,
  deleteFileRequestById,
};
export default FileRequestAPI;
